import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../../../hooks/useToast'
import type {
  UpdateAccessListSuccessResponse,
  UpdateAccessListErrorResponse,
  MediaLibraryCollectionAccessListSuccessResponse,
  MediaLibraryCollectionAccessListErrorResponse,
  ShareModalState,
} from '../../../../interfaces'
import { ACLEntityTypeEnum } from '../../../../constants'
import ShareModal, { UserGroupLabel, UserLabel } from './ShareModal'
import { MLContext, UserContext } from '../../../../providers'
import { useHistory } from 'react-router-dom'
import { isRestrictedUser } from '../../../../../utils/roles'

interface Props {
  assetId: string
  assetName: string
  isOpen: boolean
  onClose(): void
}

const AccessList = ({
  assetId,
  assetName,
  isOpen = false,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()

  const { request, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const { user } = useContext(UserContext)

  const [
    { selectedBreadcrumbMediaCollection, refetchMediaCollections, refetch },
  ] = useContext(MLContext)

  const history = useHistory()

  const getMediaCollectionAccessList = async (): Promise<
    ShareModalState | undefined
  > => {
    const res = (await request.get(
      `/api/v2/media-collections/${assetId}/access-list`,
    )) as
      | MediaLibraryCollectionAccessListSuccessResponse
      | MediaLibraryCollectionAccessListErrorResponse
    if (request.error) {
      toast({
        description: t('something_went_wrong', 'Something went wrong!'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      const successfulResponse =
        res as MediaLibraryCollectionAccessListSuccessResponse
      const accessList = successfulResponse.acl
        .filter(
          aclItem => aclItem.entity.type !== ACLEntityTypeEnum.MEDIA_COLLECTION,
        )
        .map(aclItem => {
          if (aclItem.entity.type === ACLEntityTypeEnum.USER) {
            const foundUser = successfulResponse.users.find(
              userItem => userItem.userId === aclItem.entity.id,
            )
            return {
              label: (
                <UserLabel
                  userName={foundUser?.userName ?? ''}
                  userEmail={foundUser?.userEmail ?? ''}
                />
              ),
              extraInfo: foundUser,
              value: aclItem,
            }
          }
          const foundGroup = successfulResponse.userGroups.find(
            userGroup => userGroup.groupId === aclItem.entity.id,
          )
          return {
            label: (
              <UserGroupLabel
                groupLabel={foundGroup?.label ?? ''}
                members={foundGroup?.userIds.length ?? 0}
              />
            ),
            extraInfo: foundGroup,
            value: aclItem,
          }
        })
      const users = successfulResponse.users
        .filter(
          userItem =>
            !(successfulResponse.createdBy.userId === userItem.userId) &&
            !successfulResponse.acl.find(
              alreadyExistingAclItem =>
                alreadyExistingAclItem.entity.id === userItem.userId &&
                alreadyExistingAclItem.entity.type === ACLEntityTypeEnum.USER,
            ),
        )
        .map(userItem => ({
          label: (
            <UserLabel
              userName={userItem.userName}
              userEmail={userItem.userEmail}
            />
          ),
          extraInfo: userItem,
          value: {
            entity: { id: userItem.userId, type: ACLEntityTypeEnum.USER },
          },
        }))
      const userGroups = successfulResponse.userGroups
        .filter(
          userGroupItem =>
            !successfulResponse.acl.find(
              alreadyExistingAclItem =>
                alreadyExistingAclItem.entity.id === userGroupItem.groupId &&
                alreadyExistingAclItem.entity.type ===
                  ACLEntityTypeEnum.USER_GROUP,
            ),
        )
        .map(userGroupItem => ({
          label: (
            <UserGroupLabel
              groupLabel={userGroupItem.label}
              members={userGroupItem.userIds.length}
            />
          ),
          extraInfo: userGroupItem,
          value: {
            entity: {
              id: userGroupItem.groupId,
              type: ACLEntityTypeEnum.USER_GROUP,
            },
          },
        }))
      const createdBy = successfulResponse.createdBy
      return {
        accessList,
        users,
        userGroups,
        createdBy,
      }
    }
  }
  const updateMediaCollectionAccessList = async (
    requestBody,
  ): Promise<void> => {
    const res = (await request.patch(
      `api/v2/media-collections/${assetId}`,
      requestBody,
    )) as UpdateAccessListSuccessResponse | UpdateAccessListErrorResponse
    if (request.error) {
      // do error handling
      toast({
        description: t('something_went_wrong', 'Something went wrong!'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return
    }

    if (
      (
        requestBody?.acl?.remove.filter(obj => obj?.entity?.id === user?.id) ||
        []
      ).length > 0 &&
      selectedBreadcrumbMediaCollection &&
      isRestrictedUser(user?.roles)
    ) {
      history.replace(`/media-collections`)
      await new Promise(resolve => setTimeout(resolve, 1500))
      await refetchMediaCollections()
    } else {
      // no-op
    }

    toast({
      description: t('successfully_updated', 'Successfully updated'),
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })
  }

  return (
    <ShareModal
      assetName={assetName}
      assetType="media-collection"
      isOpen={isOpen}
      onClose={onClose}
      getAccessList={getMediaCollectionAccessList}
      updateAccessList={updateMediaCollectionAccessList}
      loadingAccessList={loading}
    />
  )
}

export default AccessList
